import { NornRefusal } from "@norn/sdk"
import { useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import { usePrincipal as useSharedPrincipal, type WidgetActionCall, type WidgetBindingRead, type WidgetHost, WidgetHostError } from "@norn/widget-renderer/runtime"
import { bindingSubscriptions } from "@norn/widget-renderer/live"
import { CellUnreachable, errorNotice, run, scopeQuery } from "@/lib/api"
import type { Scope } from "@/lib/connection"
import { NoticeError, useNotice } from "@/lib/i18n"
import { stringifyExact } from "@/lib/lossless"
import { useCell } from "@/lib/session"

export * from "@norn/widget-renderer/runtime"

/** Admin's authenticated SDK is the adapter; the shared WidgetIR runtime sees only this host port. */
export function useAdminWidgetHost(scope: Scope, onNavigate?: (pageId: string, params: Record<string, string>) => void, widgetId = ""): WidgetHost {
  const { connection, client } = useCell()
  const navigate = useNavigate()
  const notice = useNotice()
  const { orgId, environmentId } = scope
  return useMemo(() => {
    const scope = { orgId, environmentId }
    const adaptError = (error: unknown): unknown => {
      if (error instanceof NornRefusal) {
        return new WidgetHostError(error.refusal.message, {
          message: error.refusal.message,
          explanation: error.refusal.explanation,
          code: error.refusal.code,
          category: error.refusal.category,
        }, error.status)
      }
      return error
    }
    // Every binding is executed by the cell (#3211); page context and record keys travel as query parameters.
    const readBinding = async ({ bindingId, query, cursor }: WidgetBindingRead, signal?: AbortSignal) => {
      try {
        return await run(connection.baseUrl, () =>
          client.GET("/v1/widgets/{widgetId}/bindings/{bindingId}", {
            params: { path: { widgetId, bindingId }, query: { ...scopeQuery(scope), ...query, ...(cursor ? { cursor } : {}) } as never },
            parseAs: "text",
            ...(signal ? { signal } : {}),
          }),
        )
      } catch (error) {
        throw adaptError(error)
      }
    }
    return {
      // One cache namespace per cell and widget.
      cacheKey: `${connection.baseUrl}#${widgetId}#${crypto.randomUUID()}`,
      subscribeBinding: bindingSubscriptions({ baseUrl: connection.baseUrl, orgId, environmentId,
        ...(connection.token ? { token: connection.token } : {}),
        ...(connection.actor ? { actor: connection.actor } : {}),
      }, widgetId),
      readBinding,
      action: async ({ bindingId: _bindingId, actionId, input, actor }: WidgetActionCall) => {
        const body = stringifyExact({ scope, actor, input, idempotencyKey: crypto.randomUUID() })
        try {
          await run(connection.baseUrl, () => client.POST("/v1/actions/{action}", {
            params: { path: { action: actionId } },
            body: {} as never,
            bodySerializer: () => body,
          }))
        } catch (error) {
          throw adaptError(error)
        }
      },
      principal: async () => {
        try {
          const principal = await run(connection.baseUrl, () => client.GET("/v1/principal", { params: { query: scopeQuery(scope) } }))
          return { actor: principal.actor, personKey: principal.personKey ?? null }
        } catch (error) {
          throw adaptError(error)
        }
      },
      ...(onNavigate ? { navigate: onNavigate } : {}),
      // Rendered when shown, so the wording follows a language switch.
      describeError: (error) => (error instanceof CellUnreachable || error instanceof NoticeError ? notice(errorNotice(error)) : undefined),
      explain: (objectType, key) => void navigate({
        to: "/$orgId/$environmentId/objects/$objectType/$key",
        params: { ...scope, objectType, key },
      }),
      openGraph: (objectType, key) => void navigate({
        to: "/$orgId/$environmentId/graph",
        params: scope,
        search: { root: `${objectType}:${key}` },
      }),
    }
  }, [client, connection, navigate, notice, onNavigate, orgId, environmentId, widgetId])
}

/** Keep the admin route API unary while sharing principal loading and state with WidgetIR pages. */
export function usePrincipal(scope: Scope) {
  const host = useAdminWidgetHost(scope)
  return useSharedPrincipal(scope, host)
}

export type { WidgetScope } from "@norn/widget-renderer/runtime"
